import React, { useMemo, useState } from 'react';
import { ErrorBoundary } from '@components/common';
import { get } from 'lodash';
import { Button, Input, Select, Space, Tooltip, Checkbox, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useAtomValue } from 'jotai';
import {
  transformFilterMetaToFields,
  transformFilterRulesToQuery,
  convertQueryToFilterRules,
  queryValidator,
  type Query,
} from '@components/filter/components/common/FilterRulesBuilder';
import { DataSource, parseRawCategory } from '@components/filter/components/utils';
import { APP_DIMENSION_NAME, VIDEO_DIMENSION_NAME, TOOLTIP_FOR_HIDDEN } from './constants';
import FilterRules from './FilterRules';
import * as atoms from '../../atom';
import * as Types from '../../../types';
import * as Styled from './styled';

export interface FilterBuilderPanelProps {
  id?: number;
  filter?: Types.Filter;
  saveButtonText?: string;
  cancelButtonText?: string;
  onSave: (filter: Types.Filter, id?: number) => Promise<void>;
  onCancel: () => void;
}

type CategoryOptions = {
  originalLabel: string;
  label: React.ReactNode;
  options: {
    originalLabel: string;
    label: React.ReactNode;
    value: string;
  }[];
}[];

const formatCategoryStr = (category: string, subcategory: string) => `${category}/${subcategory}`;

const getMatchedCategory = (
  query: Query,
  filterMeta: Types.FilterMeta,
  categoryOptions: CategoryOptions,
) => {
  const defaultSubCategory = get(categoryOptions, '[0].options[0].value');

  try {
    const firstRuleField = get(query, 'rules[0].field') ?? get(query, 'rules[0].rules[0].field');
    const parentDimensionsMeta = filterMeta.dimensions.find((d) =>
      d.fields.some((it) => it.value === firstRuleField),
    );
    const fieldMeta = parentDimensionsMeta?.fields.find((it) => it.value === firstRuleField);

    if (!parentDimensionsMeta || !fieldMeta) return defaultSubCategory;

    for (let i = 0; i < categoryOptions.length; i++) {
      const targetCategory = categoryOptions[i];
      // check if subcategory matched
      const matchedSubcategory = targetCategory.options.find(
        (it) => (fieldMeta.name || '').toLowerCase() === it.originalLabel.toLowerCase(),
      );
      if (matchedSubcategory) return matchedSubcategory.value;

      // check if category matched
      if (
        parentDimensionsMeta.name.toLowerCase().includes(targetCategory.originalLabel.toLowerCase())
      ) {
        return targetCategory.options[0].value;
      }
    }
  } catch (e) {
    // do nothing
  }

  return defaultSubCategory;
};

const checkFilterErrors = (name: string, query: Query) => {
  const isValidQuery = queryValidator(query);
  const errors = {} as Record<
    'name' | 'query',
    {
      valid: boolean;
      message?: string;
    }
  >;
  if (!isValidQuery) {
    const message = !query.rules?.length ? 'Please add filter rules' : undefined;
    errors.query = {
      valid: false,
      message,
    };
  }
  if (!name) {
    errors.name = {
      valid: false,
      message: 'Filter name is required',
    };
  } else if (!/^[0-9A-Za-z]/.test(name)) {
    errors.name = {
      valid: false,
      message: 'The first character must be alphanumeric',
    };
  }
  return errors;
};

export const FilterBuilderPanel = (props: FilterBuilderPanelProps) => {
  const { id, filter, saveButtonText, cancelButtonText, onSave, onCancel } = props;
  const [showError, setShowError] = useState(false);
  const user = useAtomValue(atoms.user);
  const filterMeta = useAtomValue(atoms.filterMeta);
  const dataSource = useAtomValue(atoms.dataSource);
  const disabledOperators = useAtomValue(atoms.disabledOperators);
  const isFilterCreate = useAtomValue(atoms.filterCreate);
  const fields = useMemo(
    () => transformFilterMetaToFields(filterMeta, { disabledOperators }),
    [filterMeta, disabledOperators],
  );

  // For app it would be platform, for video/ad it would be device name
  const defaultField =
    dataSource === DataSource.app
      ? fields.find((it) => it.name === APP_DIMENSION_NAME.platform)
      : fields.find((it) => it.name === VIDEO_DIMENSION_NAME.deviceName);

  const [query, setQuery] = useState(() =>
    transformFilterRulesToQuery(filter?.rule || [], fields, defaultField),
  );

  // Filter name
  const [currentName, setCurrentName] = useState(filter?.name || '');

  // Filter hidden
  // Pick the value of filter.hidden first, by default it use the value of user.hidden
  const [hidden, setHidden] = useState<boolean | undefined>(filter?.hidden ?? user.hidden);

  const categoryOptions = useMemo(() => {
    const { categories } = filterMeta;
    return Object.entries(categories)
      .map(([category, subCategories]) => {
        return {
          originalLabel: category ?? '',
          label: category ?? '',
          options: subCategories
            .map((it) => ({
              originalLabel: String(it ?? ''),
              label: <Styled.CategoryLabel>{it}</Styled.CategoryLabel>,
              value: `${category}/${it}`,
            }))
            .sort((a, b) => a.originalLabel.localeCompare(b.originalLabel)),
        };
      })
      .sort((a, b) => a.originalLabel.localeCompare(b.originalLabel));
  }, [filterMeta]) as CategoryOptions;

  // Filter category & subcategory
  const [currentCategoryStr, setCurrentCategoryStr] = useState(() =>
    filter
      ? formatCategoryStr(filter.category, filter.subcategory)
      : getMatchedCategory(query, filterMeta, categoryOptions),
  );

  const autoUpdateCategoryStr = (str) => {
    if (!isFilterCreate) return;
    setCurrentCategoryStr(str);
  };

  const errors = checkFilterErrors(currentName, query);
  const handleOnSave = () => {
    if (Object.keys(errors).length) {
      if (errors.query?.message) {
        message.warning(errors.query.message);
      }
      const firstErrorInput = document.querySelector('.filter-builder-panel .error-input');
      firstErrorInput?.scrollIntoView();
      setShowError(true);
      return;
    }
    setShowError(false);

    const { category, subcategory } = parseRawCategory(currentCategoryStr!);
    const currentFilter = {
      name: currentName,
      rule: convertQueryToFilterRules(query),
      category,
      subcategory,
    } as Types.Filter;
    if (typeof hidden === 'boolean') {
      Object.assign(currentFilter, { isHiddenFromCustomer: hidden });
    }

    onSave(currentFilter, id);
  };

  const renderHiddenCheckBox = () => {
    if (!user.hidden) return null;
    return (
      <Styled.FormItemContainer style={{ paddingLeft: '60px' }}>
        <Space direction="vertical">
          <Styled.FormItemLabel>
            <Space>
              Hide
              <Tooltip
                title={TOOLTIP_FOR_HIDDEN}
                style={{
                  marginLeft: '3px',
                }}
              >
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          </Styled.FormItemLabel>
          <Checkbox
            checked={hidden}
            onChange={(e) => {
              setHidden(e.target.checked ?? false);
            }}
          />
        </Space>
      </Styled.FormItemContainer>
    );
  };

  return (
    <ErrorBoundary>
      <Styled.Container className="filter-builder-panel">
        <Styled.Wrapper>
          <Styled.FormItemContainer>
            <FilterRules
              defaultField={defaultField}
              fields={fields}
              query={query}
              showError={showError}
              onChange={(q) => {
                setQuery(q);
                setShowError(false);
                autoUpdateCategoryStr(getMatchedCategory(q, filterMeta, categoryOptions));
              }}
            />
          </Styled.FormItemContainer>
          <Styled.FormItemContainer style={{ paddingLeft: '60px' }}>
            <Space direction="vertical">
              <Styled.FormItemLabel required>Filter Name</Styled.FormItemLabel>
              <Input
                maxLength={128}
                placeholder="Please enter filter name"
                className={errors.name && showError ? 'error-input' : ''}
                style={{
                  width: '400px',
                }}
                value={currentName}
                onChange={(e) => {
                  setCurrentName(e.target.value);
                  setShowError(false);
                }}
              />
              {errors.name && showError ? (
                <Styled.ErrorMessage>{errors.name.message}</Styled.ErrorMessage>
              ) : null}
            </Space>
            <Space direction="vertical">
              <Styled.FormItemLabel required>Category</Styled.FormItemLabel>
              <Select
                showSearch
                style={{
                  width: '250px',
                }}
                value={currentCategoryStr}
                options={categoryOptions}
                onChange={(data) => setCurrentCategoryStr(data)}
              />
            </Space>
          </Styled.FormItemContainer>
          {renderHiddenCheckBox()}
          <Styled.FormItemContainer
            style={{ paddingTop: '20px', borderTop: '1px solid rgba(153, 153, 153, 0.3)' }}
          >
            <Button ghost type="primary" onClick={handleOnSave}>
              {saveButtonText}
            </Button>
            <Button onClick={onCancel}>{cancelButtonText || 'Cancel'}</Button>
          </Styled.FormItemContainer>
        </Styled.Wrapper>
      </Styled.Container>
    </ErrorBoundary>
  );
};
